import { SmartCardProvider } from '@atlaskit/link-provider';
import { EngagementProvider } from '@atlassiansox/engagekit-ts';
import { analyticsClient, getDatabaseContainerType } from '@databases/analytics';
import { BannerProvider } from '@databases/banner/BannerContext';
import { CommentProvider } from '@databases/comments/context/CommentContext';
import { initializeCommentsPreloaders } from '@databases/comments/preloaders';
import { ContentStatusObserver } from '@databases/confluence-shell';
import {
  getCloudId,
  getConfluenceWorkspaceId,
  getVersionId,
  initializeContentSyncMachine,
  useDatabaseContent,
} from '@databases/database-state';
import {
  ConfirmDialog,
  type ConfirmDialogProps,
  DialogProvider,
} from '@databases/dialog';
import { type DialogsType } from '@databases/dialog/src/context/DialogContext';
import { useLocale } from '@databases/intl';
import { MediaClientCacheProvider } from '@databases/media-client';
import { isStandalone } from '@databases/modes';
import { PanelProvider } from '@databases/panel/PanelContext';
import { MenuPortal } from '@databases/react/menuPortal';
import { appStore } from '@databases/redux/store';
import { container } from '@databases/service-container';
import UnsupportedVersionCheck from '@databases/unsupported-version/UnsupportedVersionCheck';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { ViewProvider } from '@databases/views/ViewContext';
import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';

import { getExperimentValue } from '@canvas/client-feature-flags';
import { dynamicImport } from '@canvas/dynamic-import';
import { smartLinkClient } from '@canvas/smart-link-client';

import ErrorBoundary from './common/components/ErrorBoundary';
import AppStyle from './common/components/GlobalAppStyles';
import { initializePreloaders } from './common/preloaders';

interface AppProps extends React.PropsWithChildren {
  aaid?: string;
  isValueEmbed?: boolean;
}

const ContentStatusObserverWithId = () => {
  const { contentId } = useDatabaseContent();
  const [updateContentStatus, setUpdateContentStatus] = useState<
    ((data: { status: string; tooltipText: string | undefined }) => void) | undefined
  >(undefined);
  const [versionName, setVersionName] = useState<string | undefined>(undefined);

  const versionId = getVersionId();

  useEffect(() => {
    const loadFromBridgeAPI = async () => {
      const bridgeAPI = await getBridgeAPI();
      setUpdateContentStatus(bridgeAPI.updateBoardStatus);
      setVersionName(bridgeAPI.getVersionName?.());
    };
    if (isStandalone()) {
      loadFromBridgeAPI();
    }
  }, []);

  return contentId ? (
    <ContentStatusObserver
      contentId={contentId}
      versionName={versionName}
      versionId={versionId}
      updateContentStatus={updateContentStatus}
    />
  ) : null;
};

const DatabaseSnackbar = lazy(() =>
  dynamicImport(() => import('@databases/snackbar/DatabaseSnackbar'))
);
const ExportDialog = lazy(() =>
  dynamicImport(() => import('@databases/export/ExportDialog'))
);
const ImportDialog = lazy(() =>
  dynamicImport(() => import('@databases/import-ui/ImportDialog'))
);
const ConnectionImportOptionsDialog = lazy(() =>
  dynamicImport(() => import('@databases/database-setup/ImportOptionsDialog'))
);

const DefaultDialogs = {
  ConfirmDialog: (props: ConfirmDialogProps) => <ConfirmDialog {...props} />,
  ExportDialog: () => (
    <Suspense>
      <ExportDialog />
    </Suspense>
  ),
  ImportDialog: () => (
    <Suspense>
      <ImportDialog />
    </Suspense>
  ),
  ConnectionImportOptionsDialog: () => (
    <Suspense>
      <ConnectionImportOptionsDialog />
    </Suspense>
  ),
} as DialogsType;

const AppWithContext: React.FC<AppProps> = ({
  aaid = '',
  children,
  isValueEmbed = false,
}) => {
  initializeContentSyncMachine();
  initializePreloaders();
  initializeCommentsPreloaders();

  const locale = useLocale();

  const engageKitConfig = {
    cloudId: getCloudId() ?? '',
    aaid,
    locale,
    product: 'confluence',
    stargateUrl: '/gateway/api',
    workspaceId: getConfluenceWorkspaceId(),
  };

  const { contentId, unsavedViewId, savedViewId } = useDatabaseContent();

  const openPreviewPanel = useCallback(
    async (url: string, ari: string): Promise<void> => {
      const rovoBridge = container.resolve('PreviewPanelAFMBridge');

      const result = await rovoBridge.isPreviewPanelAvailable({
        ari,
      });

      if (result) {
        rovoBridge.openPreviewPanel({ url, ari });
      } else {
        globalThis.open(url, '_blank', 'noopener, noreferrer');
      }
    },
    []
  );

  const handleUnsavedViewLoaded = useCallback(() => {
    analyticsClient.sendUnsavedViewLoadedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
    });
  }, [contentId]);

  const onUnsavedViewLoaded = useMemo(() => {
    if (!contentId || !unsavedViewId) {
      return undefined;
    }

    return handleUnsavedViewLoaded;
  }, [contentId, handleUnsavedViewLoaded, unsavedViewId]);

  return (
    <MenuPortal>
      <ErrorBoundary>
        <EngagementProvider
          config={engageKitConfig}
          analyticsClientInstance={analyticsClient.client}
        >
          <BannerProvider>
            <SmartCardProvider
              client={smartLinkClient}
              isPreviewPanelAvailable={() =>
                getExperimentValue('databasesPreviewPanel', 'isEnabled', {
                  fireExperimentExposure: true,
                })
              }
              openPreviewPanel={({ url, ari }) => openPreviewPanel(url, ari)}
            >
              <MediaClientCacheProvider>
                <Provider store={appStore}>
                  <CommentProvider>
                    <ViewProvider
                      contentId={contentId}
                      initialUnsavedViewId={unsavedViewId}
                      urlSavedViewId={savedViewId}
                      onUnsavedViewLoaded={onUnsavedViewLoaded}
                    >
                      <ContentStatusObserverWithId />
                      <AppStyle isValueEmbed={isValueEmbed} />
                      <UnsupportedVersionCheck />
                      <Suspense>
                        <DatabaseSnackbar />
                      </Suspense>
                      <DialogProvider defaultDialogs={DefaultDialogs}>
                        <PanelProvider>{children}</PanelProvider>
                      </DialogProvider>
                    </ViewProvider>
                  </CommentProvider>
                </Provider>
              </MediaClientCacheProvider>
            </SmartCardProvider>
          </BannerProvider>
        </EngagementProvider>
      </ErrorBoundary>
    </MenuPortal>
  );
};

export default AppWithContext;
